import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Crown, Trash2, Scale, Loader2, Tag, FileDown } from "lucide-react";

const L = (lang, zh, en) => (lang === "zh" ? zh : en);

// Normalize a saved record (kind phone | lucky) into a comparable shape
function normalize(rec) {
  const r = rec.result || {};
  const number = r.phone || r.full || rec.label || "—";
  const score = typeof r.score === "number" ? r.score : null;
  const dominant = r.dominant || "—";
  const segments = r.segments || r.pairs || [];
  const matched = r.matched_intents || (rec.ai_reading?.suitable_intents) || [];
  return { id: rec.id, kind: rec.kind, number, score, dominant, segments, matched, label: rec.label, note: rec.note || "" };
}

export const NumberCompare = () => {
  const { lang } = useI18n();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get("/numerology/saved", { params: { kind: "phone" } }).then((r) => r.data.items).catch(() => []),
      api.get("/numerology/saved", { params: { kind: "lucky" } }).then((r) => r.data.items).catch(() => []),
    ]).then(([p, l]) => {
      const all = [...p, ...l]
        .map(normalize)
        .filter((x) => x.score !== null)
        .sort((a, b) => b.score - a.score);
      setItems(all);
      setSelected((prev) => {
        if (prev.length) return prev.filter((id) => all.some((x) => x.id === id));
        return all.slice(0, Math.min(3, all.length)).map((x) => x.id);
      });
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/numerology/saved/${id}`); toast.success(L(lang, "已删除", "Deleted")); load(); }
    catch { toast.error("Error"); }
  };
  const saveNote = async (id, note) => {
    try { await api.patch(`/numerology/saved/${id}/note`, { note }); setItems((arr) => (arr || []).map((x) => x.id === id ? { ...x, note } : x)); }
    catch { toast.error("Error"); }
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportPdf = () => {
    const chosenList = (items || []).filter((x) => selected.includes(x.id));
    if (chosenList.length === 0) { toast.error(L(lang, "请先选择要对比的号码", "Select numbers first")); return; }
    const bestId = chosenList.reduce((m, x) => (x.score > (m?.score ?? -1) ? x : m), null)?.id;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const zh = lang === "zh";
    const cards = chosenList.map((it) => {
      const isBest = it.id === bestId && chosenList.length > 1;
      const segs = (it.segments || []).map((s) => `<span class="seg ${s.type === "auspicious" ? "g" : "v"}" title="${esc(s.pair)} ${esc(s.star)}"></span>`).join("");
      const tags = (it.matched || []).map((m) => `<span class="tag">${esc(m)}</span>`).join("");
      return `<div class="card ${isBest ? "best" : ""}">
        ${isBest ? `<div class="crown">${zh ? "★ 最旺" : "★ Best"}</div>` : ""}
        <div class="num">${esc(it.number)}</div>
        ${it.note ? `<div class="note">${esc(it.note)}</div>` : ""}
        <div class="score">${it.score}<small>/100</small></div>
        <div class="lbl">${zh ? "主导磁场" : "Dominant"}</div>
        <div class="dom">${esc(it.dominant)}</div>
        ${tags ? `<div class="tags">${tags}</div>` : ""}
        <div class="segs">${segs}</div>
      </div>`;
    }).join("");
    const title = zh ? "你好功德 · 号码对比" : "CiAO Merit · Number Comparison";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
      *{box-sizing:border-box} body{font-family:-apple-system,"Noto Serif SC","Microsoft YaHei",system-ui,sans-serif;color:#2a231b;margin:0;padding:32px 40px;background:#fff}
      h1{font-size:20px;margin:0 0 4px;color:#8a6a2f} .meta{color:#6b6157;font-size:12px;margin-bottom:18px}
      .grid{display:flex;gap:14px;flex-wrap:wrap}
      .card{width:190px;border:1px solid #e7ddc9;border-radius:12px;padding:14px;position:relative}
      .card.best{border-color:#C9A227;box-shadow:0 0 0 3px rgba(201,162,39,.25)}
      .crown{display:inline-block;background:#C9A227;color:#2a231b;font-size:11px;font-weight:800;border-radius:999px;padding:2px 8px;margin-bottom:6px}
      .num{font-size:18px;font-weight:800;letter-spacing:1px;word-break:break-all}
      .note{font-size:12px;color:#8a6a2f;margin-top:2px} .score{font-size:34px;font-weight:800;color:#b23a2e;margin-top:8px} .score small{font-size:12px;color:#9a8f80}
      .lbl{font-size:11px;color:#8a7a5f;margin-top:6px} .dom{font-weight:700;color:#8a6a2f}
      .tags{margin-top:6px} .tag{display:inline-block;border:1px solid #C9A227;color:#8a6a2f;font-size:10px;border-radius:6px;padding:1px 6px;margin:2px 2px 0 0}
      .segs{margin-top:10px;display:flex;flex-wrap:wrap;gap:3px} .seg{width:20px;height:6px;border-radius:3px;display:inline-block} .seg.g{background:#C9A227} .seg.v{background:#b23a2e}
      .foot{margin-top:22px;color:#9a8f80;font-size:11px;text-align:center;border-top:1px solid #eee;padding-top:10px}
      @media print{.noprint{display:none} body{padding:16px}}
    </style></head><body>
      <button class="noprint" onclick="window.print()" style="float:right;background:#C9A227;color:#2a231b;border:none;border-radius:8px;padding:8px 14px;font-weight:700;cursor:pointer">${zh ? "打印 / 存为 PDF" : "Print / Save as PDF"}</button>
      <h1>${esc(title)}</h1>
      <div class="meta">${zh ? "生成时间" : "Generated"}: ${new Date().toLocaleString()}</div>
      <div class="grid">${cards}</div>
      <div class="foot">你好功德 CiAO Merit</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},350)}</script>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error(L(lang, "请允许弹出窗口以导出 PDF", "Please allow pop-ups to export PDF")); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  if (items === null) return <div className="py-10 text-center text-muted-foreground flex flex-col items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] p-4 text-sm" data-testid="compare-empty">
        {L(lang, "还没有可对比的号码。在「数字能量」或「吉利号码」分析后点「加入对比」，即可在这里并排比较，挑出最旺的号码。",
          "No numbers to compare yet. Use 'Add to compare' on the Energy or Lucky tabs, then compare them side-by-side here.")}
      </div>
    );
  }

  const chosen = items.filter((x) => selected.includes(x.id));
  const best = chosen.reduce((m, x) => (x.score > (m?.score ?? -1) ? x : m), null);

  return (
    <div className="space-y-5" data-testid="number-compare">
      {/* Selector list */}
      <div>
        <div className="flex items-center gap-2 mb-2 text-[hsl(var(--brand-bronze))]"><Scale className="h-4 w-4" /><span className="text-sm font-semibold">{L(lang, "选择要对比的号码", "Pick numbers to compare")}</span></div>
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map((it) => (
            <div key={it.id} className={`rounded-lg border p-2.5 transition-colors ${selected.includes(it.id) ? "border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.18)]" : "border-border"}`} data-testid={`compare-pick-${it.id}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => toggle(it.id)} />
                <span className="font-mono-num font-bold tracking-wider text-[hsl(var(--brand-ink))] flex-1 truncate">{it.number}</span>
                <Badge className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]">{it.score}</Badge>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--brand-vermillion))]" onClick={(e) => { e.preventDefault(); del(it.id); }} data-testid={`compare-del-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
              </label>
              <div className="flex items-center gap-1.5 mt-2">
                <Tag className="h-3.5 w-3.5 text-[hsl(var(--brand-bronze))] shrink-0" />
                <Input
                  defaultValue={it.note}
                  placeholder={L(lang, "备注，如 车牌 / 新号", "Note, e.g. car plate / new SIM")}
                  className="h-7 text-xs"
                  data-testid={`compare-note-${it.id}`}
                  onBlur={(e) => { if (e.target.value !== it.note) saveNote(it.id, e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Side-by-side comparison */}
      {chosen.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm font-semibold text-[hsl(var(--brand-bronze))]">{L(lang, "并排对比", "Side-by-side")}</span>
            <Button size="sm" variant="outline" onClick={exportPdf} className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="compare-export">
              <FileDown className="h-4 w-4 mr-1" />{L(lang, "导出 PDF", "Export PDF")}
            </Button>
          </div>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-3 min-w-min" data-testid="compare-grid">
            {chosen.map((it) => {
              const isBest = best && it.id === best.id && chosen.length > 1;
              return (
                <Card key={it.id} className={`shrink-0 w-56 ${isBest ? "border-[hsl(var(--brand-gold))] ring-2 ring-[hsl(var(--brand-gold)/0.4)]" : "border-border"}`} data-testid={`compare-card-${it.id}`}>
                  <CardContent className="p-4">
                    {isBest && (
                      <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-gold))] px-2.5 py-0.5 text-xs font-bold text-[hsl(var(--brand-ink))]" data-testid={`compare-best-${it.id}`}>
                        <Crown className="h-3.5 w-3.5" />{L(lang, "最旺", "Best")}
                      </div>
                    )}
                    <div className="font-mono-num text-lg font-bold tracking-wider text-[hsl(var(--brand-ink))] break-all">{it.number}</div>
                    {it.note && (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-bronze))]" data-testid={`compare-card-note-${it.id}`}>
                        <Tag className="h-3 w-3" />{it.note}
                      </div>
                    )}
                    <div className="mt-3 flex items-end gap-1">
                      <span className="font-mono-num text-4xl font-bold text-[hsl(var(--brand-vermillion))]">{it.score}</span>
                      <span className="text-xs text-muted-foreground mb-1.5">/100</span>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">{L(lang, "主导磁场", "Dominant")}</div>
                    <div className="font-display font-semibold text-[hsl(var(--brand-bronze))]">{it.dominant}</div>
                    {(it.matched || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {it.matched.map((m, i) => <Badge key={i} variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))] text-[10px]">{m}</Badge>)}
                      </div>
                    )}
                    {(it.segments || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {it.segments.map((s, i) => (
                          <span key={i} className={`h-1.5 w-6 rounded-full ${s.type === "auspicious" ? "bg-[hsl(var(--brand-gold))]" : "bg-[hsl(var(--brand-vermillion))]"}`} title={`${s.pair} ${s.star}`} />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            </div>
          </div>
        </div>
      )}
      {chosen.length > 1 && best && (
        <p className="text-sm text-[hsl(var(--brand-bronze))]" data-testid="compare-verdict">
          {L(lang, `综合评分最高：`, `Highest score: `)}<span className="font-mono-num font-bold">{best.number}</span>（{best.score}）
        </p>
      )}
    </div>
  );
};
